import React from 'react';

import Framework from "@beardeddevops/react.framework";
import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import moment from "moment";
import SignaturePad from "react-signature-canvas";
import {Button, Input} from "reactstrap";
import {
	ACQUISITION_KIOSK_MODE,
	clearAcquisitionKioskSession,
	getAcquisitionFormsPath,
	getAcquisitionKioskPath,
	getAcquisitionKioskSession,
	setAcquisitionKioskSession,
} from "constants/AcquisitionKiosk";

const DEFAULT_RI060_SIGNATURE_PREFERENCE = "print_signature";

class AcquisitionKiosk extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			collectionId: '',
			currentStep: 'loading',
			signaturePreference: DEFAULT_RI060_SIGNATURE_PREFERENCE,
			location: '',
			location_binary_id: '',
			hasSigned: false,
			hasAccountPin: !!props.rootState?.hasPin,
			pin_1: '',
			pin_2: '',
			pin_3: '',
			pin_4: '',
			error: '',
			isLoading: false,
			isLoggingOut: false,
			sessionMissing: false,
		};

		this.sigPad = React.createRef();
		this.pinRefs = [
			React.createRef(),
			React.createRef(),
			React.createRef(),
			React.createRef()
		];
	}

	componentDidMount() {
		this.restoreSessionState();
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.state.currentStep === 'unlocking' && prevState.currentStep !== 'unlocking') {
			setTimeout(() => {
				this.pinRefs[0].current?.focus();
			}, 100);
		}
	}

	restoreSessionState = () => {
		const routeCollectionId = this.props.match?.params?.id || '';
		const session = getAcquisitionKioskSession();

		if (session?.collectionId && routeCollectionId && session.collectionId !== routeCollectionId) {
			this.props.history.replace(getAcquisitionKioskPath(session.collectionId));
			return;
		}

		if (session?.collectionId && !routeCollectionId) {
			this.props.history.replace(getAcquisitionKioskPath(session.collectionId));
			return;
		}

		const collectionId = session?.collectionId || routeCollectionId || '';
		const sessionMissing = !session || !session.collectionId || (!!routeCollectionId && session.collectionId !== routeCollectionId);

		this.setState({
			collectionId,
			currentStep: sessionMissing ? 'unlocking' : (session.currentStep || 'signing'),
			signaturePreference: session?.signaturePreference || DEFAULT_RI060_SIGNATURE_PREFERENCE,
			location: session?.location || '',
			location_binary_id: session?.location_binary_id || '',
			hasSigned: !!session?.hasSigned,
			hasAccountPin: !!this.props.rootState?.hasPin,
			error: '',
			isLoading: false,
			sessionMissing,
		}, () => {
			if (!sessionMissing && this.state.currentStep === 'signing') {
				this.sigPad.current?.clear();
			}
		});
	};

	updateSession = (override = {}) => {
		const session = getAcquisitionKioskSession();

		if (!session) {
			return;
		}

		setAcquisitionKioskSession({
			...session,
			...override,
		});
	};

	getCollectionIdentifiers = () => {
		const collectionId = this.state.collectionId;
		const [collection_binary_id, collection] = collectionId.split('-');
		return {collection, collection_binary_id};
	};

	getFullPin = () => {
		return this.state.pin_1 + this.state.pin_2 + this.state.pin_3 + this.state.pin_4;
	};

	clearPin = () => {
		this.setState({
			pin_1: '',
			pin_2: '',
			pin_3: '',
			pin_4: '',
		});
	};

	handlePinChange = (index) => (event) => {
		let value = event.target.value;

		if (value.length > 1) {
			value = value.slice(-1);
		}

		if (value !== '' && !/^\d$/.test(value)) {
			return;
		}

		const pinKey = `pin_${index + 1}`;
		this.setState({[pinKey]: value}, () => {
			if (value !== '' && index < 3) {
				this.pinRefs[index + 1].current?.focus();
			}
		});
	};

	handlePinKeyDown = (index) => (event) => {
		const pinKey = `pin_${index + 1}`;

		if (event.key === 'Backspace' && this.state[pinKey] === '' && index > 0) {
			this.pinRefs[index - 1].current?.focus();
		}

		if (event.key === 'Enter' && this.state.currentStep === 'unlocking') {
			const pin = this.getFullPin();
			if (pin.length === 4 && !this.state.isLoading) {
				event.preventDefault();
				this.handleUnlock();
			}
		}
	};

	handleClearSignature = () => {
		this.sigPad.current?.clear();
		this.setState({error: ''});
	};

	handleEmergencyLogout = async (event) => {
		if (typeof event !== 'undefined') {
			event.preventDefault();
		}

		if (this.state.isLoggingOut) {
			return false;
		}

		this.setState({isLoggingOut: true, error: ''});

		try {
			if (this.props.rootState.isAdmin) {
				let model = new API.BuildClass(Types.Admins_Locations._name, API.RequestType.READ);
				await model.submit(null, API.RequestType.DELETE);
				let otherDomains = API.Domains.filter((domain) => domain !== API.URL);
				for (let domain of otherDomains) {
					try {
						model.submit(null, API.RequestType.DELETE, false, false, false, domain).then();
					} catch (e) {}
				}
				clearAcquisitionKioskSession();
				this.props.rootState.triggerAction('logout_admin');
			} else {
				let model = new API.BuildClass(Types.Accounts._name, API.RequestType.READ);
				await model.submit(null, API.RequestType.DELETE);
				let otherDomains = API.Domains.filter((domain) => domain !== API.URL);
				for (let domain of otherDomains) {
					try {
						model.submit(null, API.RequestType.DELETE, false, false, false, domain).then();
					} catch (e) {}
				}
				clearAcquisitionKioskSession();
				this.props.rootState.triggerAction('logout');
			}
		} catch (error) {
			this.setState({
				error: 'Failed to log out',
				isLoggingOut: false,
			});
		}

		return false;
	};

	handleSaveSignature = async () => {
		if (this.state.sessionMissing) {
			this.setState({
				currentStep: 'unlocking',
				error: 'Signing session was interrupted. Enter your PIN to unlock the device.',
			});
			return;
		}

		const sigPad = this.sigPad.current;
		if (sigPad?.isEmpty()) {
			this.setState({error: 'Please provide a signature'});
			return;
		}

		const {collection, collection_binary_id} = this.getCollectionIdentifiers();
		if (!collection || !collection_binary_id || !this.state.location || !this.state.location_binary_id) {
			this.setState({
				currentStep: 'unlocking',
				error: 'Signing session is missing RI-060 details. Enter your PIN to unlock the device.',
			});
			return;
		}

		try {
			this.setState({isLoading: true, error: ''});

			const signatureData = sigPad.getTrimmedCanvas().toDataURL('image/png');
			const signatureDate = moment().local().format('YYYY-MM-DD HH:mm:ss');

			const sigModel = new API.BuildClass(Types.Transactions_Collections._name, API.RequestType.CREATE);
			sigModel.request = 'PUT_SIGNATURE';
			sigModel.object.signature_ri_060 = signatureData;
			sigModel.object.signature_ri_060_date = signatureDate;
			sigModel.object.collection = collection;
			sigModel.object.collection_binary_id = collection_binary_id;
			sigModel.object.location = this.state.location;
			sigModel.object.location_binary_id = this.state.location_binary_id;

			await sigModel.submit(null, 'PUT_SIGNATURE');

			this.updateSession({
				currentStep: 'unlocking',
				hasSigned: true,
			});

			this.clearPin();
			this.setState({
				currentStep: 'unlocking',
				hasSigned: true,
				isLoading: false,
				error: '',
			});
		} catch (error) {
			this.setState({
				error: 'Failed to save signature',
				isLoading: false,
			});
		}
	};

	createRi060Form = async (collectionId, location, locationBinaryId, signaturePreference) => {
		if (!collectionId || !location || !locationBinaryId) {
			return;
		}

		const formsModel = new API.BuildClass(Types.Transactions_Collections_Forms._name, API.RequestType.CREATE);
		formsModel.parent = collectionId;
		formsModel.object.form = 26;
		formsModel.object.send_email = false;
		formsModel.object.location = location;
		formsModel.object.location_binary_id = locationBinaryId;
		formsModel.object.ri060_signature_preference = signaturePreference;
		await formsModel.submit(null, API.RequestType.CREATE);
	};

	handleUnlock = async () => {
		const pin = this.getFullPin();
		if (pin.length !== 4) {
			this.setState({error: 'Please enter your PIN'});
			return;
		}

		try {
			this.setState({isLoading: true, error: ''});

			const unlockModel = new API.BuildClass(Types.Accounts._name, API.RequestType.READ);
			unlockModel.object.kiosk_mode = ACQUISITION_KIOSK_MODE;
			unlockModel.object.pin = pin;
			await unlockModel.submit(null, 'DELETE_LOCK');

			const session = getAcquisitionKioskSession();
			const collectionId = session?.collectionId || this.state.collectionId;
			const location = session?.location || this.state.location;
			const locationBinaryId = session?.location_binary_id || this.state.location_binary_id;
			const signaturePreference = session?.signaturePreference || this.state.signaturePreference;
			let nextPath = collectionId ? `/acquisitions/${collectionId}` : '/acquisitions';

			clearAcquisitionKioskSession();
			this.clearPin();

			if (collectionId && location && locationBinaryId && !this.state.sessionMissing) {
				try {
					await this.createRi060Form(collectionId, location, locationBinaryId, signaturePreference);
					nextPath = getAcquisitionFormsPath(collectionId);
				} catch (error) {
					API.Toast.error('Unlocked the device, but failed to generate the RI-060 form.', {
						position: API.Toast.POSITION.TOP_RIGHT,
						autoClose: 5000
					});
				}
			}

			this.props.rootState.triggerAction('getUserInformation', this.props.location);
			this.props.history.replace(nextPath);
		} catch (error) {
			this.setState({
				error: 'Invalid PIN',
				isLoading: false,
			});
			this.clearPin();
		}
	};

	renderPinInputs = () => {
		return (
			<div className="d-flex justify-content-center gap-2 flex-wrap">
				{[0, 1, 2, 3].map((index) => {
					const pinKey = `pin_${index + 1}`;
					return (
						<Input
							key={index}
							innerRef={this.pinRefs[index]}
							type="text"
							inputMode="numeric"
							maxLength={1}
							value={this.state[pinKey]}
							onChange={this.handlePinChange(index)}
							onKeyDown={this.handlePinKeyDown(index)}
							style={{
								width: '60px',
								height: '60px',
								fontSize: '24px',
								textAlign: 'center',
								fontWeight: 'bold'
							}}
						/>
					);
				})}
			</div>
		);
	};

	render() {
		const {currentStep, error, hasAccountPin, hasSigned, isLoading, isLoggingOut, sessionMissing} = this.state;
		const isUnlocking = currentStep === 'unlocking';

		if (currentStep === 'loading') {
			return <Framework.Elements.LoadingSpinner isLoading={true}/>;
		}

		return (
			<div
				className="container-fluid d-flex align-items-center justify-content-center"
				style={{minHeight: '100vh', backgroundColor: '#f4f6f8', padding: '24px', position: 'relative'}}
			>
				<div style={{position: 'fixed', top: '24px', right: '24px', zIndex: 10}}>
					<Button
						color="danger"
						size="sm"
						onClick={this.handleEmergencyLogout}
						disabled={isLoggingOut}
					>
						<i className="fa fa-sign-out mr-1"/>
						{isLoggingOut ? 'Logging Out...' : 'Logout'}
					</Button>
				</div>

				<div
					className="card shadow-sm"
					style={{width: '100%', maxWidth: '760px', borderRadius: '16px'}}
				>
					<div className="card-body p-4 p-md-5">
						<div className="text-center mb-4">
							<h2 className="mb-2">{isUnlocking ? 'Unlock Device' : 'Customer Signature Required'}</h2>
							<p className="text-muted mb-0">
								{isUnlocking
									? 'Only PIN unlock is available until this acquisition kiosk session ends.'
									: 'Only the RI-060 signature flow is available while the customer is signing.'}
							</p>
						</div>

						{error && (
							<div className="alert alert-danger">{error}</div>
						)}

						{sessionMissing && (
							<div className="alert alert-warning">
								This signing session was interrupted. Signature capture is no longer available; enter the PIN to unlock the device.
							</div>
						)}

						{!isUnlocking && (
							<>
								<p className="text-center mb-3">
									Please sign below to generate the Michigan RI-060 form.
								</p>
								<div
									className="border rounded mx-auto"
									style={{
										width: '100%',
										maxWidth: '600px',
										backgroundColor: '#fff'
									}}
								>
									<SignaturePad
										ref={this.sigPad}
										canvasProps={{
											width: 600,
											height: 250,
											className: "signature-canvas w-100"
										}}
									/>
								</div>

								<div className="text-center mt-3">
									<Button
										color="secondary"
										size="sm"
										onClick={this.handleClearSignature}
										disabled={isLoading}
									>
										<i className="fa fa-eraser mr-1"/>
										Clear Signature
									</Button>
								</div>

								<div className="text-center mt-4">
									<Button
										color="success"
										size="lg"
										onClick={this.handleSaveSignature}
										disabled={isLoading || sessionMissing}
									>
										{isLoading ? 'Saving...' : 'Submit Signature'}
									</Button>
								</div>
							</>
						)}

						{isUnlocking && (
							<>
								{hasSigned && (
									<div className="alert alert-success">
											<i className="fa fa-check-circle mr-2"/>
										Signature captured successfully.
									</div>
								)}
								<p className="text-center mb-4">
									Enter your PIN to unlock the device.
								</p>
								{this.renderPinInputs()}
								{hasAccountPin && (
									<p className="text-muted small text-center mt-3 mb-0">
										PIN must match the saved account PIN.
									</p>
								)}
								<div className="text-center mt-4">
									<Button
										color="success"
										size="lg"
										onClick={this.handleUnlock}
										disabled={isLoading || this.getFullPin().length !== 4}
									>
										{isLoading ? 'Unlocking...' : 'Unlock'}
									</Button>
								</div>
							</>
						)}
					</div>
				</div>

				<Framework.Elements.LoadingSpinner isLoading={isLoading}/>
			</div>
		);
	}
}

export default Framework.Components.withRootState(AcquisitionKiosk);
